import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { memo, useRef, useState } from 'react';

import AuctioneerInfo from '@/components/AuctioneerInfo/AuctioneerInfo';
import { ItemCard } from '@/components/ItemCard/ItemCard';
import { useAppSelector } from '@/redux/hooks';
import { getConversation } from '@/redux/modules/conversation/conversations/conversation.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';

import { AuctionInfo } from './AuctionInfo';
import { ContextHeader } from './ContextHeader';

type Props = {
    conversationId: number;
};

const containerClass = 'mb-xs pb-xs [&_a]:no-underline first:border-b first:border-border-secondary print:hidden';

const ContextComponent = ({ conversationId }: Props) => {
    const { itemId } = useAppSelector((state) => getConversation(state, Number(conversationId)));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));

    const targetRef = useRef(null);
    const [componentOffset, setComponentOffset] = useState(200);
    const [initalOffset, setInitalOffset] = useState(200);
    const [scrolled, setScrolled] = useState(false);

    useScrollPosition(
        () => {
            if (!scrolled) {
                setScrolled(true);
            }
            // @ts-ignore
            if (!componentOffset && targetRef?.current?.getBoundingClientRect()?.top) {
                // @ts-ignore
                setInitalOffset(targetRef?.current?.getBoundingClientRect()?.top || 0);
            }
            // @ts-ignore
            setComponentOffset(targetRef?.current?.getBoundingClientRect()?.top || 0);
        },
        [scrolled],
        targetRef,
        false,
        50
    );

    const contextRef = useRef<HTMLDivElement>(null);
    // @ts-ignore
    const height = contextRef?.current?.clientHeight || 0;
    const [windowHeight, setWindowHeight] = useState(1500);

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const newWindowHeight = document?.documentElement?.scrollHeight;
        if (windowHeight !== newWindowHeight) {
            setWindowHeight(newWindowHeight || 0);
        }
    }

    const stick = Boolean(componentOffset <= 0) && scrolled;
    const position = componentOffset;

    // if the element nears the top of the window, stick it
    let style: { [attribute: string]: string | number } = stick
        ? { maxWidth: 224, position: 'fixed', top: 0, width: 224 }
        : {};

    // if we get to the bottom, switch to sticking to the bottom of the container
    if (windowHeight - 1116 - initalOffset + position - height <= 0 && scrolled) {
        style = { bottom: 10, position: 'absolute' };
    }

    return (
        <div
            className="relative max-w-[247px] min-w-[247px] pl-md"
            ref={targetRef}
        >
            <div
                className="print:flex print:flex-row"
                ref={contextRef}
                style={style}
            >
                {Boolean(sellerId) && (
                    <div className={containerClass}>
                        <ContextHeader messageId="auctioneer" />
                        <AuctioneerInfo
                            hideAskQuestionBtn
                            hideFollowBtn
                            hideFollowers
                            hideImage
                            openOnBlank
                            sellerId={sellerId}
                        />
                    </div>
                )}
                {Boolean(catalogId) && (
                    <div className={containerClass}>
                        <ContextHeader messageId="auction" />
                        <AuctionInfo
                            itemId={itemId}
                            openOnBlank
                        />
                    </div>
                )}
                {Boolean(itemId) && (
                    <div className="mb-xs pb-xs first:border-b first:border-border-secondary [&_a]:no-underline">
                        <ItemCard
                            allowBidding
                            hideSeller
                            itemId={itemId}
                            openOnBlank
                            showBidStatus
                            showCta={!isPassed && !isSold && bidderHasBid}
                            showDate={false}
                            showEstimate
                            showImageBorder
                            showImageWithHover
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export const Context = memo<Props>(ContextComponent);
